import React, { FC, Fragment, Suspense, createContext, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import routesPath from './RoutesPath';
import { BottomBar, SideNav, TopBar } from 'containers';
import {
  selectChatIconState,
  selectCurrentTeam,
  selectIsActionsBlocked,
  selectMenuState,
  selectUserDetails
} from 'containers/app/appSlice';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import { useAppSelector, useScreenDimensions } from 'hooks';
import { If, Loader } from 'components';
import { useLazyGetPaymentSystemConfigQuery, useLazyGetTeamBankAccountQuery } from 'containers/app/api';
import { useLazyCheckCapitalFinancingEnabledQuery } from 'containers/capital/api';
import {
  useLazyGetFsmSubscriptionQuery,
  useLazyListSubscriptionPlansQuery
} from 'containers/integrations/fsm-integration/api';
import { hideChatIcon, showChatIcon } from 'services/freshchat';
import {
  isDuplicateEstimateURL,
  isDuplicateItemURL,
  isDuplicateJobURL,
  isEditCategoryURL,
  isEditCommissionRuleURL,
  isEditEstimateURL,
  isEditExternalCompanyURL,
  isEditInvoiceURL,
  isEditItemURL,
  isEditJobURL,
  isEditServiceAreaURL,
  isEditServiceChargeURL,
  isEditTagURL,
  isEditTaxURL,
  isMemberReviewPage,
  isPayPeriodReviewURL,
  isRespondToDisputeUrl,
  isReviewSubmitDisputeUrl,
  isUploadEvidenceUrl,
  isViewDisputeEvidenceUrl
} from './URLRegex';
import { chatIconHiddenRoutes, fullScreenRoutes } from './constants';
// Page imports - eager loaded
import HomePage from 'pages/HomePage';
import InvoicesPage from 'pages/InvoicesPage';
import JobsPage from 'pages/JobsPage';
import ReceiptsPage from 'pages/ReceiptsPage';
import EstimatesPage from 'pages/EstimatesPage';
import JobDetailsPage from 'pages/JobDetailsPage';
import CreateInvoicePage from 'pages/CreateInvoicePage';
import EditInvoicePage from 'pages/EditInvoicePage';
import CreateEditEstimate from 'containers/estimates/CreateEditEstimate';
import CreateJobPage from 'pages/CreateJobPage';
import CustomersPage from 'pages/CustomersPage';

import BlockerBanner from 'containers/home/components/BlockerBanner';
import { SubscriptionStatus } from 'containers/app/app.model';
import GlobalModalRenderer from 'containers/subscriptions/GlobalModalRenderer';
import GlobalBannerRenderer from 'containers/subscriptions/GlobalBannerRenderer';
import { shouldShowFSMModal } from 'containers/subscriptions/utils';

// Panel imports - eager loaded
import SharePendingInvoice from 'containers/invoices/components/SharePendingInvoicePanel';
import CollectPaymentPanel from 'containers/invoices/components/CollectPaymentPanel';
import ShareQRPanel from 'containers/invoices/components/ShareQRPanel';
import ShareUrlPanel from 'containers/invoices/components/PendingInvoiceUrlSharePanel';
import UpdateStatusPanel from 'containers/jobs/components/UpdateStatusPanel';
import EstimatesPanel from 'containers/estimates/components/EstimatesPanel';
import ShareEstimatePanel from 'containers/estimates/components/ShareEstimatePanel';
import PayoutTransactionsPanel from 'containers/payouts/components/PayoutTransactionsPanel';
import HeldInReservePanel from 'containers/payouts/payouts-balance/components/HeldInReservePanel';
import JobReviewPanel from 'containers/accounting/pay-runs/components/JobReviewPanel';
// eslint-disable-next-line max-len
import PayPeriodRecentActivity from 'containers/accounting/pay-runs/pay-period-review/components/PayPeriodRecentActivity';
import InvoicePanel from 'containers/invoices/components/InvoicePanel';
import InvoiceDetailsPanel from 'containers/invoice-details/InvoiceDetailsPanel';
import InvoiceHistoryPanel from 'containers/invoices/components/InvoiceHistoryPanel';
import CustomerPanel from 'containers/invoices/components/CustomerPanel';
import ShareReceiptPanel from 'containers/invoices/components/ShareReceiptPanel';
import RecordPaymentPanel from 'containers/invoices/components/RecordPaymentPanel';
import DeleteInvoicePanel from 'containers/invoices/components/DeleteInvoicePanel';
import IssueRefundPanel from 'containers/invoices/components/IssueRefundPanel';
import ReceiptDetailsPanel from 'containers/receipts/components/ReceiptDetailsPanel';
import CreateRefundPanel from 'containers/receipts/components/CreateRefundPanel';
import InitiateRefundPanel from 'containers/receipts/components/InitiateRefundPanel';
import RefundSuccessPanel from 'containers/receipts/components/RefundSuccessPanel';
import RefundReceiptPanel from 'containers/receipts/components/RefundReceiptPanel';
import RefundFailedPanel from 'containers/receipts/components/RefundFailedPanel';
import DisputeDetailsPanel from 'containers/disputes/components/DisputePanel';
import TotalBalanceDetailsPanel from 'containers/payouts/payouts-balance/components/TotalBalanceDetailsPanel';
import UpComingPayoutDetailsPanel from 'containers/payouts/payouts-balance/components/UpcomingPayoutDetailsPanel';
import PayoutDetailsPanel from 'containers/payouts/components/PayoutDetailsPanel';
// eslint-disable-next-line max-len
import OnTheWayPayoutsListPanel from 'containers/payouts/payouts-balance/components/OnTheWayPayoutsListPanel';
import ApplicationStatusPage from 'containers/payouts/payouts-settings/components/ApplicationStatusPage';
import ConnectOnboarding from 'containers/payouts/payouts-settings/components/ConnectOnboarding';
import BankingOnboarding from 'containers/banking/BankingOnboarding';
import { DefaultPrivateLayoutContextProps } from './types';

// Lazy loaded pages
const CapitalPage = lazy(() => import('pages/CapitalPage'));
const MyAccountPage = lazy(() => import('pages/MyAccountPage'));
const ItemsPage = lazy(() => import('pages/ItemsPage'));
const CategoriesPage = lazy(() => import('pages/CategoriesPage'));
const TaxesPage = lazy(() => import('pages/TaxesPage'));
const PayoutSettingsPage = lazy(() => import('containers/payouts/payouts-settings/PayoutsSettingsPage'));
const AccountsPage = lazy(() => import('pages/AccountsPage'));
const BankingPage = lazy(() => import('pages/BankingPage'));
const DebitCardsPage = lazy(() => import('pages/DebitCardsPage'));
const InvoiceSettingsPage = lazy(() => import('pages/InvoiceSettingsPage'));
const PaymentSettingsPage = lazy(() => import('pages/PaymentSettingsPage'));
const MapViewsSettingsPage = lazy(() => import('pages/MapViewsSettingsPage'));
const TaxFormsPage = lazy(() => import('pages/TaxFormsPage'));
const TagsPage = lazy(() => import('pages/TagsPage'));
const CreateTagPage = lazy(() => import('pages/CreateTagPage'));
const ManageTeamPage = lazy(() => import('pages/ManageTeamPage'));
const TeamProfilePage = lazy(() => import('pages/TeamProfilePage'));
const InviteMemberPage = lazy(() => import('pages/InviteMemberPage'));
const EstimateIntegrationPage = lazy(() => import('pages/EstimateIntegrationPage'));
const EstimateSettingsPage = lazy(() => import('pages/EstimateSettingsPage'));
const CreateCommissionRulePage = lazy(() => import('pages/CreateCommissionRulePage'));
const CommissionSettingsPage = lazy(() => import('pages/CommissionSettingsPage'));
const MapsPage = lazy(() => import('pages/MapsPage'));
const SettingsPage = lazy(() => import('pages/SettingsPage'));
const ReportsPage = lazy(() => import('pages/ReportsPage'));
const CreateTaxPage = lazy(() => import('pages/CreateTaxPage'));
const EditTaxPage = lazy(() => import('pages/EditTaxPage'));
const CreateItemPage = lazy(() => import('pages/CreateItemPage'));
const EditItemPage = lazy(() => import('pages/EditItemPage'));
const DuplicateItemPage = lazy(() => import('pages/DuplicateItemPage'));
const CreateCategoryPage = lazy(() => import('pages/CreateCategoryPage'));
const EditCategoryPage = lazy(() => import('pages/EditCategoryPage'));
const CreateServiceChargePage = lazy(() => import('pages/CreateServiceChargePage'));
const EditServiceChargePage = lazy(() => import('pages/EditServiceChargePage'));
const ServiceChargesPage = lazy(() => import('pages/ServiceChargesPage'));
const DisputesPage = lazy(() => import('pages/DisputesPage'));
const RespondToDisputePage = lazy(() => import('pages/RespondToDisputesPage'));
const DisputeUploadEvidencePage = lazy(() => import('pages/DisputeUploadEvidence'));
const DisputesReviewAndSubmitPage = lazy(() => import('pages/DisputesReviewAndSubmitPage'));
const DisputesViewEvidencePage = lazy(() => import('pages/DisputesViewEvidencePage'));
const IntegrationsPage = lazy(() => import('pages/IntegrationsPage'));
const DeveloperAPIPage = lazy(() => import('pages/DeveloperAPIPage'));
const WebhooksPage = lazy(() => import('pages/WebhooksPage'));
const QuickbooksPage = lazy(() => import('pages/QuickbooksPage'));
const QBOIntuitLoginPage = lazy(() => import('containers/integrations/quikcbooks/components/IntuitLogin'));
const QBOInitialSettingsPage = lazy(() => import('containers/integrations/quikcbooks/components/QBOInitialSettings'));
const PayoutsHomePage = lazy(() => import('pages/PayoutsBalancePage'));
const CreateTeamPage = lazy(() => import('pages/CreateTeamPage'));
// Add this page when the lendica feature is enabled
// const BusinessLoansPage = lazy(() => import('pages/BusinessLoansPage'));
const MemberProfilePage = lazy(() => import('pages/MemberProfilePage'));
const AccountingPayRunsPage = lazy(() => import('pages/AccountingPayRunsPage'));
const PayPeriodReviewPage = lazy(() => import('pages/PayPeriodReviewPage'));
const PayScheduleSettingsPage = lazy(() => import('pages/PayScheduleSettingsPage'));
const AccountingExternalCompanyPage = lazy(() => import('pages/AccountingExternalCompanyPage'));
const ExternalCompanyReportPage = lazy(() => import('pages/ExternalCompanyReportPage'));
const CommissionReportPage = lazy(() => import('pages/CommissionReportPage'));
const ExpenseReportPage = lazy(() => import('pages/ExpenseReportPage'));
const PaymentReportPage = lazy(() => import('pages/PaymentReportPage'));
const TipsReportPage = lazy(() => import('pages/TipsReportPage'));
const AreaPerformanceReportPage = lazy(() => import('pages/AreaPerformanceReportPage'));
const JobProfitabilityReportPage = lazy(() => import('pages/JobProfitabilityReportPage'));
const FieldServiceManagementPage = lazy(() => import('pages/FieldServiceManagementPage'));
const AdjustmentSettingsPage = lazy(() => import('pages/AdjustmentSettingsPage'));
const EditTagPage = lazy(() => import('pages/EditTagPage'));
const ExternalCompanyPage = lazy(() => import('pages/ExternalCompanyPage'));
const AddEditCompanyProfile = lazy(() => import('containers/settings/external-company-settings/AddEditCompanyProfile'));
const PayRunsListPage = lazy(() => import('pages/PayRunsListPage'));
const MemberReviewPage = lazy(() => import('pages/MemberReviewPage'));
const ServiceAreasSettingsPage = lazy(() => import('pages/ServiceAreasSettingsPage'));
const AddEditServiceArea = lazy(() => import('containers/settings/service-areas/AddEditServiceArea'));
const PayoutsList = lazy(() => import('containers/payouts/payouts-list/PayoutsList'));
const FreshChatLazy = lazy(() => import('react-freshchat'));
interface DefaultPrivateLayoutProps {
  isLoadingGetMerchantTeams: boolean;
  isLoadingInitAPI: boolean;
}

export const DefaultPrivateLayoutContext = createContext<DefaultPrivateLayoutContextProps>({
  isFullScreenLayout: false
});

const DefaultPrivateLayout: FC<DefaultPrivateLayoutProps> = ({ isLoadingInitAPI, isLoadingGetMerchantTeams }) => {
  const location = useLocation();
  const isMenuActive = useAppSelector(selectMenuState);
  const isChatVisible = useAppSelector(selectChatIconState);
  const [isChatIconVisible, setIsChatIconVisible] = useState(true);
  const isAccountSettingsLayout = location.pathname === routesPath.ACCOUNT_SETTINGS_MY_ACCOUNT;
  const [getPaymentSystemConfigQuery] = useLazyGetPaymentSystemConfigQuery();
  const [getTeamBankAccountQuery] = useLazyGetTeamBankAccountQuery();
  const [checkCapitalFinancingEnabled] = useLazyCheckCapitalFinancingEnabledQuery();
  const [listSubscriptionPlans] = useLazyListSubscriptionPlansQuery();
  const [getFsmSubscription] = useLazyGetFsmSubscriptionQuery();

  const currentUser = useAppSelector(selectUserDetails);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);

  const { viewportHeightInPx } = useScreenDimensions();

  const isEditURL =
    isEditInvoiceURL.test(location.pathname) ||
    isEditTaxURL.test(location.pathname) ||
    isEditItemURL.test(location.pathname) ||
    isDuplicateItemURL.test(location.pathname) ||
    isEditCategoryURL.test(location.pathname) ||
    isEditServiceChargeURL.test(location.pathname) ||
    isRespondToDisputeUrl.test(location.pathname) ||
    isUploadEvidenceUrl.test(location.pathname) ||
    isReviewSubmitDisputeUrl.test(location.pathname) ||
    isViewDisputeEvidenceUrl.test(location.pathname) ||
    isEditJobURL.test(location.pathname) ||
    isEditEstimateURL.test(location.pathname) ||
    isDuplicateEstimateURL.test(location.pathname) ||
    isEditCommissionRuleURL.test(location.pathname) ||
    isEditTagURL.test(location.pathname) ||
    isEditExternalCompanyURL.test(location.pathname) ||
    isDuplicateJobURL.test(location.pathname) ||
    isMemberReviewPage.test(location.pathname) ||
    isEditServiceAreaURL.test(location.pathname);

  const hasFullScreenLayout = isPayPeriodReviewURL.test(location.pathname);

  const isChatIconHiddenRoute = chatIconHiddenRoutes.includes(location.pathname) || isEditURL;

  const shouldHideChatIcon = isChatIconHiddenRoute || !isChatVisible;

  const isFullScreenLayout = fullScreenRoutes.includes(location.pathname) || isEditURL || hasFullScreenLayout;

  const isJobDetails = location.pathname.includes('/jobs/');

  const showSubscriptionModal = shouldShowFSMModal(location.pathname) ?? false;
  const shouldMountChat = !shouldHideChatIcon && Boolean(process.env.REACT_APP_FRESHCHAT_TOKEN);

  useEffect(() => {
    // TODO Create a wrapper for fresh chat and chat functionality instead of using chat apis directly in code
    if (shouldHideChatIcon) {
      hideChatIcon();
      setIsChatIconVisible(false);
    } else {
      if (!isChatIconVisible) {
        showChatIcon();
        setIsChatIconVisible(true);
      }
    }
  }, [location.pathname, shouldHideChatIcon]);

  useEffect(() => {
    if (currentTeam.id) {
      getPaymentSystemConfigQuery(currentTeam.id);
      getTeamBankAccountQuery(currentTeam.id);
      checkCapitalFinancingEnabled({ teamId: currentTeam.id });
      listSubscriptionPlans({ teamId: currentTeam.id });
      getFsmSubscription({ teamId: currentTeam.id });
    }
  }, [currentTeam.id]);

  const initialiseFreshChat = widget => {
    widget.user.setFirstName(currentUser.name);
    widget.user.setLastName(' ');
    widget.user.setEmail(currentUser.email);
    widget.user.setPhone(currentUser.phone);
    widget.setExternalId(currentUser.id);
    widget.user.setProperties({
      merchantId: currentUser.id,
      currentTeam: currentTeam.name,
      currentTeamId: currentTeam.id
    });
    if (shouldHideChatIcon) {
      widget?.setConfig({
        headerProperty: {
          hideChatButton: true
        }
      });
    }
  };

  const isEstimateSubscribed = currentTeam.subscriptionStatus === SubscriptionStatus.SUBSCRIBED;

  const shouldShowBlockedBanner = () => {
    if (location.pathname.includes(routesPath.ESTIMATES) && isEstimateSubscribed) {
      return false;
    }
    if (!isFullScreenLayout && isActionsBlocked) {
      return true;
    }
    return false;
  };

  return (
    <Fragment>
      {isLoadingInitAPI ? (
        <div className="flex h-screen w-full">
          <Loader />
        </div>
      ) : (
        <div
          style={{ height: viewportHeightInPx }}
          className={'flex w-screen flex-col overflow-hidden bg-primary sm:flex-col'}>
          <If condition={!isFullScreenLayout}>
            <TopBar className={` hidden sm:flex ${showSubscriptionModal ? 'z-[52]' : 'z-50'}`} />
          </If>
          <div className="flex h-full flex-row overflow-hidden">
            <If condition={!isFullScreenLayout}>
              <SideNav />
            </If>
            <div
              className={twMerge(
                'flex h-full w-full flex-col ml-0',
                classnames({
                  'ml-[100px] w-[calc(100%_-_100px)]': !isMenuActive && !isFullScreenLayout,
                  'lg:customNormalScroll overflow-y-auto': !isAccountSettingsLayout,
                  'pointer-events-none select-none': showSubscriptionModal
                })
              )}>
              {shouldShowBlockedBanner() && <BlockerBanner />}
              <GlobalBannerRenderer isFullScreenLayout={isFullScreenLayout} />
              {isLoadingGetMerchantTeams ? (
                <div className="flex h-full items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center">
                      <Loader />
                    </div>
                  }>
                  <Routes>
                    <Route path={routesPath.HOME} element={<HomePage />} />
                    <Route path={routesPath.JOB_BY_ID} element={<JobDetailsPage />}>
                      <Route path={routesPath.JOB_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.JOB_INVOICE_BY_ID} element={<InvoicePanel />} />
                      <Route path={routesPath.JOB_BY_ID_EDIT_STATUS} element={<UpdateStatusPanel />} />
                      <Route path={routesPath.JOB_INVOICE_PENDING_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.JOB_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.JOB_INVOICE_PENDING_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.JOB_INVOICE_PENDING_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.JOB_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.JOB_INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.JOB_RECEIPT_BY_ID} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.JOB_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.JOB_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.JOB_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.JOB_PAYOUT} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.JOB_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.JOB_ESTIMATE} element={<EstimatesPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.JOB_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.JOB_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.JOB_INVOICE_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route path={routesPath.JOB_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.JOB_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                    </Route>
                    <Route path={routesPath.JOB_JOB_DETAILS} element={<JobDetailsPage />} />
                    <Route path={routesPath.JOBS} element={<JobsPage />} />
                    <Route path={routesPath.CREATE_JOB} element={<CreateJobPage />} />
                    <Route path={routesPath.EDIT_JOB} element={<CreateJobPage isEdit={true} />} />
                    <Route path={routesPath.DUPLICATE_JOB} element={<CreateJobPage />} />

                    <Route path={routesPath.INVOICES} element={<InvoicesPage />}>
                      <Route path={routesPath.INVOICE_BY_ID} element={<InvoicePanel />} />
                      <Route path={routesPath.INVOICE_BY_ID_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.INVOICE_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.INVOICE_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.INVOICE_INVOICE_BY_ID} element={<InvoicePanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.INVOICE_PENDING_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.INVOICE_PENDING_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.INVOICE_PENDING_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route
                        path={routesPath.INVOICE_PENDING_SHARE_COLLECT_PAYMENT}
                        element={<CollectPaymentPanel />}
                      />
                      <Route path={routesPath.INVOICE_SHARE_PENDING_INVOICE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.INVOICE_SHARE_PENDING_INVOICE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.INVOICE_SHARE_PENDING_INVOICE_URL} element={<ShareUrlPanel />} />
                      <Route
                        path={routesPath.INVOICE_SHARE_PENDING_INVOICE_COLLECT_PAYMENT}
                        element={<CollectPaymentPanel />}
                      />

                      <Route path={routesPath.INVOICE_BY_ID_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route
                        path={routesPath.INVOICE_INVOICE_BY_ID_COLLECT_PAYMENT}
                        element={<CollectPaymentPanel />}
                      />

                      <Route path={routesPath.INVOICE_RECEIPT_SHARE_INVOICE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.INVOICE_SHARE_INVOICE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.INVOICE_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.INVOICE_RECORD_PAYMENT_INVOICE} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.INVOICE_DELETE_INVOICE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.INVOICE_TXN_DETAIL} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.INVOICE_PAYOUT} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.INVOICE_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.INVOICE_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.INVOICE_PAYOUT_TXNS} element={<PayoutTransactionsPanel />} />
                      <Route path={routesPath.INVOICE_ESTIMATE} element={<EstimatesPanel />} />
                      <Route path={routesPath.INVOICE_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.INVOICE_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                      <Route path={routesPath.INVOICE_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                    </Route>
                    <Route path={routesPath.INVOICE_JOB_BY_ID} element={<JobDetailsPage />} />
                    <Route path={routesPath.INVOICES_CREATE} element={<CreateInvoicePage />} />
                    <Route path={routesPath.INVOICES_EDIT} element={<EditInvoicePage />} />

                    <Route path={routesPath.RECEIPTS} element={<ReceiptsPage />}>
                      <Route path={routesPath.RECEIPT_BY_ID} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_BY_ID} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.RECEIPT_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE} element={<InvoicePanel />} />
                      <Route path={routesPath.RECEIPT_PAYOUT_BY_ID} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.RECEIPT_PENDING_INVOICE_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.RECEIPT_PENDING_INVOICE_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.RECEIPT_PENDING_INVOICE_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.RECEIPT_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.RECEIPT_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.RECEIPT_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.RECEIPT_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.RECEIPT_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.RECEIPT_PAYOUT_BY_ID_TXNS} element={<PayoutTransactionsPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.RECEIPT_ESTIMATE} element={<EstimatesPanel />} />
                      <Route path={routesPath.RECEIPT_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.RECEIPT_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                    </Route>
                    <Route path={routesPath.RECEIPT_JOB_DETAILS} element={<JobDetailsPage />} />

                    <Route path={routesPath.PAYOUTS}>
                      <Route path={routesPath.PAYOUTS_BALANCE} element={<PayoutsHomePage />}>
                        <Route path={routesPath.PAYOUTS_BALANCE_TOTAL_DETALS} element={<TotalBalanceDetailsPanel />} />
                        <Route
                          path={routesPath.PAYOUTS_BALANCE_UPCOMING_PAYOUTS}
                          element={<UpComingPayoutDetailsPanel />}
                        />
                        <Route path={routesPath.PAYOUTS_BALANCE_HELD_IN_RESERVE} element={<HeldInReservePanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_PAYOUT_BY_ID} element={<PayoutDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_RECEIPT_DETAIL} element={<ReceiptDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_PAYOUT_BY_ID} element={<PayoutDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_INVOICE} element={<InvoicePanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                        <Route
                          path={routesPath.PAYOUT_BALANCE_PENDING_INVOICE_SHARE}
                          element={<SharePendingInvoice />}
                        />
                        <Route path={routesPath.PAYOUT_BALANCE_PENDING_INVOICE_SHARE_QR} element={<ShareQRPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_PENDING_INVOICE_SHARE_URL} element={<ShareUrlPanel />} />

                        <Route path={routesPath.PAYOUT_BALANCE_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                        <Route
                          path={routesPath.PAYOUT_BALANCE_RECEIPT_INITIATE_REFUND}
                          element={<InitiateRefundPanel />}
                        />
                        <Route
                          path={routesPath.PAYOUT_BALANCE_RECEIPT_REFUND_SUCCESS}
                          element={<RefundSuccessPanel />}
                        />
                        <Route path={routesPath.PAYOUT_BALANCE_RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                        <Route
                          path={routesPath.PAYOUT_BALANCE_INVOICE_RECORD_PAYMENT}
                          element={<RecordPaymentPanel />}
                        />
                        <Route path={routesPath.PAYOUT_BALANCE_TXNS} element={<PayoutTransactionsPanel />} />
                        <Route
                          path={routesPath.PAYOUTS_BALANCE_UPCOMING_PAYOUTS_TXNS}
                          element={<PayoutTransactionsPanel />}
                        />
                        <Route
                          path={routesPath.PAYOUTS_BALANCE_IN_TRANSIT_PAYOUTS}
                          element={<OnTheWayPayoutsListPanel />}
                        />
                        <Route path={routesPath.PAYOUTS_BALANCE_ESTIMATE} element={<EstimatesPanel />} />
                        <Route path={routesPath.PAYOUT_BALANCE_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      </Route>

                      <Route path={routesPath.PAYOUTS_LIST} element={<PayoutsList />}>
                        <Route path={routesPath.PAYOUT_LIST_PAYOUT_BY_ID} element={<PayoutDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT} element={<ReceiptDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_CUSTOMER} element={<CustomerPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE} element={<InvoicePanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_PENDING_INVOICE_SHARE} element={<SharePendingInvoice />} />
                        <Route path={routesPath.PAYOUT_LIST_PENDING_INVOICE_SHARE_QR} element={<ShareQRPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_PENDING_INVOICE_SHARE_URL} element={<ShareUrlPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT_CREATE_REFUND} element={<CreateRefundPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_DISPUTE} element={<DisputeDetailsPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                        <Route
                          path={routesPath.PAYOUT_LIST_RECEIPT_INITIATE_REFUND}
                          element={<InitiateRefundPanel />}
                        />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_RECEIPT_REFUND_FAILED} element={<RefundFailedPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_TXNS} element={<PayoutTransactionsPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                        <Route path={routesPath.PAYOUT_LIST_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                        <Route path={routesPath.PAYOUT_LIST_ESTIMATE} element={<EstimatesPanel />} />
                      </Route>

                      <Route path={routesPath.BANK_APPLICATION_STATUS} element={<ApplicationStatusPage />} />
                      <Route path={routesPath.CONNECT_ONBOARDING} element={<ConnectOnboarding />} />
                    </Route>
                    <Route path={routesPath.PAYOUT_BALANCE_JOB_DETAILS} element={<JobDetailsPage />} />
                    <Route path={routesPath.PAYOUT_LIST_JOB_DETAILS} element={<JobDetailsPage />} />

                    <Route path={routesPath.CUSTOMERS} element={<CustomersPage />}>
                      <Route path={routesPath.CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE} element={<InvoicePanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.CUSTOMER_RECEIPT} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.CUSTOMER_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.CUSTOMER_PENDING_INVOICE_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.CUSTOMER_PENDING_INVOICE_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.CUSTOMER_PENDING_INVOICE_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.CUSTOMER_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.CUSTOMER_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.CUSTOMER_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.CUSTOMER_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.CUSTOMER_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.CUSTOMER_PAYOUT} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.CUSTOMER_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.CUSTOMER_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.CUSTOMER_PAYOUT_TXNS} element={<PayoutTransactionsPanel />} />
                      <Route path={routesPath.CUSTOMER_ESTIMATE_BY_ID} element={<EstimatesPanel />} />
                      <Route path={routesPath.CUSTOMER_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.CUSTOMER_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                    </Route>
                    <Route path={routesPath.CUSTOMER_JOB_DETAILS} element={<JobDetailsPage />} />

                    <Route path={routesPath.SETTINGS}>
                      <Route path={routesPath.MAP_VIEWS_SETTINGS} element={<MapViewsSettingsPage />} />
                      <Route path={routesPath.INVOICE_SETTINGS} element={<InvoiceSettingsPage />} />
                      <Route path={routesPath.PAYMENT_SETTINGS} element={<PaymentSettingsPage />} />
                      <Route path={routesPath.TAX_FORMS_SETTINGS} element={<TaxFormsPage />} />
                      <Route path={routesPath.ESTIMATE_SETTINGS} element={<EstimateSettingsPage />} />
                      <Route path={routesPath.PAYOUTS_SETTINGS} element={<PayoutSettingsPage />} />
                      <Route path={routesPath.SERVICE_AREAS_SETTINGS} element={<ServiceAreasSettingsPage />} />
                      <Route path={routesPath.ADD_SERVICE_AREA} element={<AddEditServiceArea />} />
                      <Route path={routesPath.EDIT_SERVICE_AREA} element={<AddEditServiceArea />} />
                      <Route path={routesPath.COMMISSION_SETTINGS} element={<CommissionSettingsPage />} />
                      <Route path={routesPath.CREATE_COMMISSION_RULE} element={<CreateCommissionRulePage />} />
                      <Route path={routesPath.EDIT_COMMISSION_RULE} element={<CreateCommissionRulePage />} />
                      <Route path={''} element={<SettingsPage />} />
                      <Route path={routesPath.TAGS} element={<TagsPage />} />
                      <Route path={routesPath.EXTERNAL_COMPANY_SETTINGS} element={<ExternalCompanyPage />} />
                      <Route path={routesPath.ADD_EXTERNAL_COMPANY} element={<AddEditCompanyProfile />} />
                      <Route path={routesPath.EDIT_EXTERNAL_COMPANY} element={<AddEditCompanyProfile />} />
                      <Route path={routesPath.PAY_SCHEDULE_SETTINGS} element={<PayScheduleSettingsPage />} />
                      <Route path={routesPath.ADJUSTMENT_SETTINGS} element={<AdjustmentSettingsPage />} />
                    </Route>
                    <Route path={routesPath.CREATE_TAG} element={<CreateTagPage />} />
                    <Route path={routesPath.EDIT_TAG} element={<EditTagPage />} />
                    <Route path={routesPath.TEAM_PROFILE} element={<TeamProfilePage />} />
                    <Route path={routesPath.CREATE_TEAM} element={<CreateTeamPage />} />
                    <Route path={routesPath.MANAGE_TEAM} element={<ManageTeamPage />}></Route>
                    <Route path={routesPath.MEMBER_PROFILE} element={<MemberProfilePage />} />
                    <Route path={routesPath.INVITED_MEMBER_PROFILE} element={<MemberProfilePage />} />
                    <Route path={routesPath.CREATE_INVITE} element={<InviteMemberPage />} />
                    <Route path={routesPath.BANKING} element={<BankingPage />} />
                    <Route path={routesPath.DEBIT_CARDS} element={<DebitCardsPage />} />
                    <Route path={routesPath.ACCOUNTS} element={<AccountsPage />} />
                    <Route path={routesPath.ALL} element={<Navigate replace={true} to={routesPath.HOME} />} />
                    <Route
                      path={routesPath.TRANSACTIONS}
                      element={<Navigate replace={true} to={`${routesPath.RECEIPTS}${location?.search}`} />}
                    />
                    <Route
                      path={routesPath.INVOICE}
                      element={<Navigate replace={true} to={`${routesPath.INVOICES}${location?.search}`} />}
                    />
                    <Route
                      path={routesPath.TEAMS_HOME}
                      element={<Navigate replace={true} to={routesPath.MANAGE_TEAM} />}
                    />
                    <Route
                      path={routesPath.BANKING_HOME}
                      element={<Navigate replace={true} to={routesPath.BANKING} />}
                    />

                    <Route path={routesPath.RESPOND_TO_DIPUTES} element={<RespondToDisputePage />} />
                    <Route path={routesPath.DISPUTES_UPDATE_EVIDENCE} element={<DisputeUploadEvidencePage />} />
                    <Route path={routesPath.DISPUTES_REVIEW_SUBMIT} element={<DisputesReviewAndSubmitPage />} />
                    <Route path={routesPath.DISPUTES_VIEW_EVIDENCE} element={<DisputesViewEvidencePage />} />
                    <Route path={routesPath.DISPUTES} element={<DisputesPage />}>
                      <Route path={routesPath.DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.DISPUTE_RECEIPT_DETAILS} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.DISPUTE_CUSTOMER} element={<CustomerPanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE} element={<InvoicePanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.DISPUTE_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.DISPUTE_PENDING_INVOICE_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.DISPUTE_PENDING_INVOICE_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.DISPUTE_PENDING_INVOICE_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.DISPUTE_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.DISPUTE_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.DISPUTE_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.DISPUTE_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.DISPUTE_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.DISPUTE_PAYOUT} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.DISPUTE_RECEIPT_REFUND} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.DISPUTE_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.DISPUTE_PAYOUT_TXNS} element={<PayoutTransactionsPanel />} />
                      <Route path={routesPath.DISPUTE_ESTIMATE_BY_ID} element={<EstimatesPanel />} />
                      <Route path={routesPath.DISPUTE_ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route path={routesPath.DISPUTE_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                    </Route>
                    <Route path={routesPath.DISPUTE_JOB_DETAILS} element={<JobDetailsPage />} />

                    <Route path={routesPath.CARDS} element={<Navigate replace={true} to={routesPath.DEBIT_CARDS} />} />
                    <Route path={routesPath.ACCOUNT_SETTINGS_MY_ACCOUNT} element={<MyAccountPage />} />
                    <Route path={routesPath.ACCOUNT_SETTINGS_CREDITS_REWARDS} element={<MyAccountPage />} />
                    <Route path={routesPath.BANKING_ONBOARDING} element={<BankingOnboarding />} />

                    <Route path={routesPath.CATALOG_ITEMS} element={<ItemsPage />} />
                    <Route path={routesPath.ITEMS_CREATE} element={<CreateItemPage />} />
                    <Route path={routesPath.ITEMS_EDIT} element={<EditItemPage />} />
                    <Route path={routesPath.ITEMS_DUPLICATE} element={<DuplicateItemPage />} />

                    <Route path={routesPath.CATALOG_CATEGORIES} element={<CategoriesPage />} />
                    <Route path={routesPath.CATEGORIES_CREATE} element={<CreateCategoryPage />} />
                    <Route path={routesPath.CATEGORIES_EDIT} element={<EditCategoryPage />} />

                    <Route path={routesPath.CATALOG_TAXES} element={<TaxesPage />} />
                    <Route path={routesPath.TAXES_CREATE} element={<CreateTaxPage />} />
                    <Route path={routesPath.TAXES_EDIT} element={<EditTaxPage />} />

                    <Route path={routesPath.CATALOG_SERVICE_CHARGES} element={<ServiceChargesPage />} />
                    <Route path={routesPath.SERVICE_CHARGES_CREATE} element={<CreateServiceChargePage />} />
                    <Route path={routesPath.SERVICE_CHARGES_EDIT} element={<EditServiceChargePage />} />

                    <Route path={routesPath.INTEGRATIONS} element={<IntegrationsPage />} />
                    <Route path={routesPath.DEVELOPER_API} element={<DeveloperAPIPage />} />
                    <Route path={routesPath.WEBHOOKS} element={<WebhooksPage />} />
                    <Route path={routesPath.QUICKBOOKS} element={<QuickbooksPage />} />
                    <Route path={routesPath.QUICKBOOKS_LOGIN} element={<QBOIntuitLoginPage />} />
                    <Route path={routesPath.QUICKBOOKS_SETTINGS} element={<QBOInitialSettingsPage />} />
                    <Route path={routesPath.ESTIMATE_INTEGRATION} element={<EstimateIntegrationPage />} />
                    <Route path={routesPath.FIELD_SERVICE_MANAGEMENT} element={<FieldServiceManagementPage />} />
                    {/* Add this route when the lendica feature is enabled */}
                    {/* <Route path={routesPath.BUSINESS_LOANS} element={<BusinessLoansPage />} /> */}
                    <Route path={routesPath.ESTIMATES} element={<EstimatesPage />}>
                      <Route path={routesPath.ESTIMATE_BY_ID} element={<EstimatesPanel />} />
                      <Route path={routesPath.ESTIMATE_CUSTOMER_BY_ID} element={<CustomerPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_BY_ID} element={<InvoicePanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_DETAILS} element={<InvoiceDetailsPanel />} />
                      <Route path={routesPath.ESTIMATE_RECEIPT_BY_ID} element={<ReceiptDetailsPanel />} />
                      <Route path={routesPath.ESTIMATE_SHARE} element={<ShareEstimatePanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_RECORD_PAYMENT} element={<RecordPaymentPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_DELETE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.ESTIMATE_DISPUTE_BY_ID} element={<DisputeDetailsPanel />} />
                      <Route path={routesPath.ESTIMATE_PAYOUT} element={<PayoutDetailsPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.ESTIMATE_PENDING_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_PENDING_SHARE} element={<SharePendingInvoice />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_PENDING_SHARE_QR} element={<ShareQRPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_PENDING_SHARE_URL} element={<ShareUrlPanel />} />
                      <Route path={routesPath.ESTIMATE_ESTIMATE_BY_ID} element={<EstimatesPanel />} />
                      <Route path={routesPath.ESTIMATE_CREATE_REFUND} element={<CreateRefundPanel />} />
                      <Route path={routesPath.ESTIMATE_INITIATE_REFUND} element={<InitiateRefundPanel />} />
                      <Route path={routesPath.ESTIMATE_REFUND_SUCCESS} element={<RefundSuccessPanel />} />
                      <Route path={routesPath.ESTIMATE_REFUND_FAILED} element={<RefundFailedPanel />} />
                      <Route path={routesPath.ESTIMATE_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.ESTIMATE_REFUND_RECEIPT_SHARE} element={<ShareReceiptPanel />} />
                      <Route path={routesPath.ESTIMATE_REFUND_RECEIPT} element={<RefundReceiptPanel />} />
                      <Route path={routesPath.ESTIMATE_DELETE_INVOICE} element={<DeleteInvoicePanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_COLLECT_PAYMENT} element={<CollectPaymentPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_HISTORY} element={<InvoiceHistoryPanel />} />
                      <Route path={routesPath.ESTIMATE_INVOICE_ISSUE_REFUND} element={<IssueRefundPanel />} />
                    </Route>
                    <Route path={routesPath.ESTIMATE_JOB_DETAILS} element={<JobDetailsPage />} />

                    <Route path={routesPath.ESTIMATES_CREATE} element={<CreateEditEstimate />} />
                    <Route path={routesPath.ESTIMATES_DUPLICATE} element={<CreateEditEstimate />} />
                    <Route path={routesPath.ESTIMATES_EDIT} element={<CreateEditEstimate />} />
                    <Route path={routesPath.MAPS} element={<MapsPage />}></Route>
                    <Route path={routesPath.ACCOUNTING_PAY_RUNS} element={<AccountingPayRunsPage />} />
                    <Route path={routesPath.PAY_PERIOD_REVIEW} element={<PayPeriodReviewPage />}>
                      <Route
                        path={routesPath.PAY_PERIOD_REVIEW_RECENT_ACTIVITY}
                        element={<PayPeriodRecentActivity />}
                      />
                    </Route>
                    <Route path={routesPath.ACCOUNTING_PAY_RUNS_LIST} element={<PayRunsListPage />} />
                    <Route path={routesPath.ACCOUNTING_PAY_RUNS_MEMBER_REVIEW} element={<MemberReviewPage />}>
                      <Route path={routesPath.ACCOUNTING_PAY_RUNS_JOB_REVIEW} element={<JobReviewPanel />} />
                    </Route>
                    <Route path={routesPath.ACCOUNTING_EXTERNAL_COMPANY} element={<AccountingExternalCompanyPage />} />
                    <Route path={routesPath.REPORTS} element={<ReportsPage />} />
                    <Route path={routesPath.COMMISSION_REPORT} element={<CommissionReportPage />} />
                    <Route path={routesPath.EXTERNAL_COMPANY_REPORT} element={<ExternalCompanyReportPage />} />
                    <Route path={routesPath.EXPENSE_REPORT} element={<ExpenseReportPage />} />
                    <Route path={routesPath.PAYMENT_REPORT} element={<PaymentReportPage />} />
                    <Route path={routesPath.TIPS_REPORT} element={<TipsReportPage />} />
                    <Route path={routesPath.AREA_PERFORMANCE_REPORT} element={<AreaPerformanceReportPage />} />
                    <Route path={routesPath.JOB_PROFITABILITY_REPORT} element={<JobProfitabilityReportPage />} />
                    <Route path={routesPath.CAPITAL_FINANCING} element={<CapitalPage />} />
                  </Routes>
                </Suspense>
              )}
            </div>
          </div>
          <DefaultPrivateLayoutContext.Provider value={{ isFullScreenLayout }}>
            <GlobalModalRenderer />
          </DefaultPrivateLayoutContext.Provider>
          {shouldMountChat && (
            <Suspense fallback={null}>
              <FreshChatLazy
                token={process.env.REACT_APP_FRESHCHAT_TOKEN}
                externalId={currentUser.id}
                onInit={widget => {
                  initialiseFreshChat(widget);
                }}
              />
            </Suspense>
          )}
          <If condition={!isFullScreenLayout && !isJobDetails}>
            <BottomBar className="fixed bottom-0 flex w-full sm:hidden" />
          </If>
        </div>
      )}
    </Fragment>
  );
};

export default DefaultPrivateLayout;
